import * as React from 'react';
import { useT } from '../lib/i18n.js';
import { DomainApiError } from '../lib/api-error.js';
import { createCalendarEvent, type CalendarEventType, type CalendarEventTypeKey } from '../lib/calendar-api.js';
/*
 * Krok KAL1 — JEDEN PŘEVOD `datetime-local` ↔ ISO, ne dva.
 *
 * `calendar-range.ts` měl vlastní dvojici stejných jmen s OPAČNÝM významem:
 * zapsané hodiny brala jako UTC (`new Date(value + 'Z')`), kdežto
 * `task-board.ts` (úkoly) jako místní čas. Termín úkolu „14:00" a termín
 * události „14:00" tak znamenaly jiný okamžik. Zůstává verze z `task-board.ts`
 * — ta je ve shodě se zbytkem administrace (`formatDateTime` kreslí místní čas)
 * i s mřížkou kalendáře po KAL1.
 */
import { fromDateTimeLocalValue, toDateTimeLocalValue } from '../lib/task-board.js';
import type { TenantMember } from '../lib/members-api.js';
import { useSession } from '../stores/session.js';
import { DateTimeField } from './date-field.js';

/**
 * Formulář nové události (krok CRM3, zadání bod 2: „vytvoření z karty
 * poptávky i ze zakázky").
 *
 * Třídy `.rh-demand-form` jsou převzaté z poptávek — stejná obrazovka jiný
 * kontext. `initial*` props existují proto, že se formulář otevírá ze tří
 * míst (kalendář, karta poptávky, zakázka) a vazby na zakázku/klienta/
 * poptávku musí být předvyplněné a ZAMČENÉ: makléř, který klikne „prohlídka"
 * u poptávky, chce událost navázanou na TU poptávku, ne na libovolnou.
 */

export interface CalendarEventFormSeed {
  readonly caseId?: string | null | undefined;
  readonly partyId?: string | null | undefined;
  readonly demandId?: string | null | undefined;
  readonly ownerUserId?: string | null | undefined;
  readonly startsAt?: string | null | undefined;
  readonly eventType?: CalendarEventTypeKey | undefined;
}

export interface CalendarEventFormProps {
  readonly types: readonly CalendarEventType[];
  readonly members: readonly TenantMember[];
  readonly seed?: CalendarEventFormSeed;
  readonly submitLabel?: string | undefined;
  readonly onCreated: () => void;
  readonly onCancel: () => void;
  /**
   * Krok UXP4c (P3, nalez „dve tlacitka Zavřít v Nove udalosti"): kalendar
   * vykresluje formular jako CELY obsah panelu, ktery uz ma svuj vlastni
   * `calendar-form-close` v hlavicce se STEJNOU akci — druhe tlacitko tu je
   * navic. V poptavce a na zakazce je formular jen VNITRNI sekce panelu bez
   * vlastni hlavicky, tam „Zavřít" zustava (vychozi `true`).
   */
  readonly showCancel?: boolean | undefined;
}

/*
 * Krok UXF3 bod 5 (#21): pripominka se vybira LIDSKY („1 den predem"), ne
 * „Pred 1440 min" — makler nema prepocitavat minuty. Hodnota poslana API
 * zustava v minutach (`reminderMinutes`), meni se jen popisek.
 */
const REMINDER_CHOICES = [
  { minutes: 0, key: 'webAdmin.calendar.drawer.reminderNone' },
  { minutes: 15, key: 'webAdmin.calendar.reminder.minutesBefore15' },
  { minutes: 60, key: 'webAdmin.calendar.reminder.hourBefore1' },
  { minutes: 1440, key: 'webAdmin.calendar.reminder.dayBefore1' },
] as const;

export function CalendarEventForm({
  types,
  members,
  seed,
  submitLabel,
  onCreated,
  onCancel,
  showCancel = true,
}: CalendarEventFormProps): React.JSX.Element {
  const t = useT();
  const [title, setTitle] = React.useState('');
  const [eventType, setEventType] = React.useState<CalendarEventTypeKey>(seed?.eventType ?? 'viewing');
  const [startsAt, setStartsAt] = React.useState(
    toDateTimeLocalValue(seed?.startsAt ?? new Date(Date.now()).toISOString()),
  );
  const [endsAt, setEndsAt] = React.useState(toDateTimeLocalValue(null));
  const [location, setLocation] = React.useState('');
  /*
   * Krok UXF3 bod 5 (#21): vychozi odpovedny makler je PRIHLASENY uzivatel,
   * ne „Vsichni makleri" (to je filtr seznamu, ne vlastnik udalosti). Seed
   * ze zakazky/poptavky ma prednost — tam vlastnika urcuje kontext.
   */
  const currentUserId = useSession((s) => s.user?.id ?? null);
  const [ownerUserId, setOwnerUserId] = React.useState(seed?.ownerUserId ?? currentUserId ?? '');
  const [reminderMinutes, setReminderMinutes] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    const starts = fromDateTimeLocalValue(startsAt);
    if (starts === null) {
      setError(t('webAdmin.calendar.form.startsAtRequired'));
      return;
    }
    const ends = fromDateTimeLocalValue(endsAt);
    try {
      await createCalendarEvent({
        title: title.trim(),
        eventType,
        startsAt: starts,
        endsAt: ends,
        location: location.trim(),
        ...(ownerUserId !== '' ? { ownerUserId } : {}),
        ...(seed?.caseId ? { caseId: seed.caseId } : {}),
        ...(seed?.partyId ? { partyId: seed.partyId } : {}),
        ...(seed?.demandId ? { demandId: seed.demandId } : {}),
        reminderMinutes,
      });
      onCreated();
    } catch (err: unknown) {
      setError(err instanceof DomainApiError ? err.message : t('webAdmin.calendar.saveFailed'));
    }
  }

  return (
    <form className="rh-demand-form" data-testid="calendar-form" onSubmit={(e) => void submit(e)}>
      {error !== null && (
        <p role="alert" className="rh-demand-drawer__error" data-testid="calendar-form-error">
          {error}
        </p>
      )}
      <label>
        {t('webAdmin.calendar.form.fieldTitle')}
        <input
          required
          autoFocus
          data-testid="calendar-form-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </label>
      <div className="rh-demand-form__row">
        <label>
          {t('webAdmin.calendar.form.type')}
          <select
            data-testid="calendar-form-type"
            value={eventType}
            onChange={(e) => setEventType(e.target.value as CalendarEventTypeKey)}
          >
            {types.map((type) => (
              <option key={type.key} value={type.key}>
                {type.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('webAdmin.calendar.form.startsAt')}
          <DateTimeField required testId="calendar-form-start" value={startsAt} onValueChange={setStartsAt} />
        </label>
        <label>
          {t('webAdmin.calendar.form.endsAt')}
          <DateTimeField testId="calendar-form-end" value={endsAt} onValueChange={setEndsAt} />
        </label>
      </div>
      <div className="rh-demand-form__row">
        <label>
          {t('webAdmin.calendar.form.location')}
          <input
            data-testid="calendar-form-location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </label>
        <label>
          {t('webAdmin.calendar.form.owner')}
          <select
            data-testid="calendar-form-owner"
            value={ownerUserId}
            onChange={(e) => setOwnerUserId(e.target.value)}
          >
            <option value="">{t('webAdmin.calendar.form.ownerUnassigned')}</option>
            {members.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('webAdmin.calendar.form.reminder')}
          <select
            data-testid="calendar-form-reminder"
            value={String(reminderMinutes)}
            onChange={(e) => setReminderMinutes(Number(e.target.value))}
          >
            {REMINDER_CHOICES.map((choice) => (
              <option key={choice.minutes} value={String(choice.minutes)}>
                {t(choice.key)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {(seed?.caseId ?? seed?.partyId ?? seed?.demandId) !== undefined &&
        (seed?.caseId || seed?.partyId || seed?.demandId) && (
          <p className="text-xs text-gray-600" data-testid="calendar-form-links">
            {[
              seed.caseId ? t('webAdmin.calendar.form.case') : null,
              seed.partyId ? t('webAdmin.calendar.form.party') : null,
              seed.demandId ? t('webAdmin.calendar.form.demand') : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
      <div className="rh-demand-drawer__moves">
        <button type="submit" className="rh-btn rh-btn--primary" data-testid="calendar-form-submit">
          {submitLabel ?? t('webAdmin.calendar.form.submit')}
        </button>
        {showCancel ? (
          <button type="button" className="rh-btn" onClick={onCancel} data-testid="calendar-form-cancel">
            {t('webAdmin.calendar.form.cancel')}
          </button>
        ) : null}
      </div>
    </form>
  );
}
